import Base from "./../base";
import { EwellUser } from "@/model/ewellUser";

export interface PortalMailLog extends Base {
  id: number | null;
  template_id: string | null;
  mail_subject: string | null;
  mail_body: string | null;
  mail_address: string | null;
  ewell_user_id?: string | null;
  portal_office_id?: number | null;
  portal_staff_id?: number | null;
  organization_id?: number | null;
  request_user_id?: number | null;
}

export interface PortalStaff extends Base {
  id: number | null;
  ewell_user_id: number | null;
  ibow_staff_id: number | null;
  portal_office_id: number | null;
  portal_office: PortalOffice | null;
  job_type: number | null;
  display_family_name: string | null;
  display_first_name: string | null;
  is_public_listing: boolean | null;
  icon: string | null;
  icon_url: string | null;
  role: number | null;
  system_id: string | null;
  ewell_user?: EwellUser | null;
}

export const DEFAULT_PORTAL_STAFF: PortalStaff = {
  id: 0,
  ewell_user_id: 0,
  ibow_staff_id: 0,
  portal_office_id: 0,
  portal_office: null,
  job_type: 0,
  display_family_name: "",
  display_first_name: "",
  is_public_listing: false,
  icon: "",
  icon_url: "",
  role: 0,
  system_id: "",
  created_at: "",
  updated_at: "",
  deleted_at: "",
};

export interface PortalOffice extends Base {
  id: number | null;
  portal_agreement_id: number | null;
  portal_agreement: PortalAgreement | null;
  ibow_office_id: number | null;
  approval_status: number | null;
  name: string | null;
  post_code: string | null;
  pref: number | null;
  city: string | null;
  area: string | null;
  building: string | null;
  tel_area: string | null;
  tel_city: string | null;
  tel_num: string | null;
  medical_institution_code: string | null;
  office_code: string | null;
  is_public: boolean | null;
  listing_review_status: number | null;
  business_start_date: string | null;
  listing_updated_at: string | null;
  listing_released_at: string | null;
  review_reason: string | null;
  fax_area: string | null;
  fax_city: string | null;
  fax_num: string | null;
  approval_datetime: string | null;
  listing_approval_datetime: string | null;
  use_carelogood?: boolean | null; // けあログっと利用
  use_jobsearch?: boolean | null; // カエドキ利用
  pending_join_service?: number | null; // 2サービス目の参加申請（0:保留なし, 1:けあログっとが保留中, 2:カエドキが保留中）
  pending_join_requested_at?: string | null; // 2サービス目の参加申請日時
}

export const DEFAULT_PORTAL_OFFICE: PortalOffice = {
  id: 0,
  portal_agreement_id: 0,
  portal_agreement: null,
  ibow_office_id: 0,
  approval_status: 0,
  name: "",
  post_code: "",
  pref: 0,
  city: "",
  area: "",
  building: "",
  tel_area: "",
  tel_city: "",
  tel_num: "",
  medical_institution_code: "",
  office_code: "",
  is_public: false,
  listing_review_status: 0,
  business_start_date: "",
  listing_updated_at: "",
  listing_released_at: "",
  review_reason: "",
  fax_area: "",
  fax_city: "",
  fax_num: "",
  approval_datetime: "",
  listing_approval_datetime: "",
  pending_join_service: 0,
  pending_join_requested_at: "",
  created_at: "",
  updated_at: "",
  deleted_at: "",
};

export interface PortalOfficeInternalRemark {
  id: number | null;
  portal_office_id: number | null;
  internal_remarks: string | null;
}

export const DEFAULT_PORTAL_OFFICE_INTERNAL_REMARK: PortalOfficeInternalRemark =
  {
    id: 0,
    portal_office_id: 0,
    internal_remarks: "",
  };

export interface PortalAgreement {
  id: number | null;
  ibow_agreement_id: number | null;
  co_name: string | null;
  post_code: string | null;
  pref: number | null;
  city: string | null;
  area: string | null;
  building: string | null;
  tel_area: string | null;
  tel_city: string | null;
  tel_num: string | null;
  fax_area: string | null;
  fax_city: string | null;
  fax_num: string | null;
  remarks: string | null;
  corporateNumber: string | null;
}

export const DEFAULT_PORTAL_AGREEMENT: PortalAgreement = {
  id: 0,
  ibow_agreement_id: 0,
  co_name: "",
  post_code: "",
  pref: 0,
  city: "",
  area: "",
  building: "",
  tel_area: "",
  tel_city: "",
  tel_num: "",
  fax_area: "",
  fax_city: "",
  fax_num: "",
  remarks: "",
  corporateNumber: "",
};

export interface PortalOrganization {
  id: number | null;
  ibow_office_id: number | null;
  approval_status: number | null;
  organization_type: number | null;
  medical_institution_code: string | null;
  office_code: string | null;
  crypto_key: string | null;
  name: string | null;
  post_code: string | null;
  pref: number | null;
  city: string | null;
  area: string | null;
  building: string | null;
  tel_area: string | null;
  tel_city: string | null;
  tel_num: string | null;
  fax_area: string | null;
  fax_city: string | null;
  fax_num: string | null;
  contact_tel_area: string | null;
  contact_tel_city: string | null;
  contact_tel_num: string | null;
  business_start_date: string | null;
  review_reason: string | null;
  created_at: string | null;
  updated_at: string | null;
  deleted_at: string | null;
  patient_ext_option?: number | null;
  card_ext_option?: number | null;
}

export const DEFAULT_PORTAL_ORGANIZATION: PortalOrganization = {
  id: 0,
  ibow_office_id: 0,
  approval_status: 0,
  organization_type: 0,
  medical_institution_code: "",
  office_code: "",
  crypto_key: "",
  name: "",
  post_code: "",
  pref: 0,
  city: "",
  area: "",
  building: "",
  tel_area: "",
  tel_city: "",
  tel_num: "",
  fax_area: "",
  fax_city: "",
  fax_num: "",
  contact_tel_area: "",
  contact_tel_city: "",
  contact_tel_num: "",
  business_start_date: "",
  review_reason: "",
  created_at: "",
  updated_at: "",
  deleted_at: "",
  patient_ext_option: 0,
  card_ext_option: 0,
};

export interface PortalOrganizationInternalRemark {
  id: number | null;
  organization_id: number | null;
  internal_remarks: string | null;
}

export const DEFAULT_PORTAL_ORGANIZATION_INTERNAL_REMARK: PortalOrganizationInternalRemark =
  {
    id: 0,
    organization_id: 0,
    internal_remarks: "",
  };

export interface RequestUser {
  id: number | null;
  ewell_user_id: number | null;
  organization_id: number | null;
  ibow_staff_id: number | null;
  display_first_name: string | null;
  display_last_name: string | null;
  icon: string | null;
  icon_url: string | null;
  role: number | null;
  office_mobile_area: string | null;
  office_mobile_city: string | null;
  office_mobile_num: string | null;
  is_office_mobile_public: boolean | null;
  system_id: string | null;
  job_type: number | null;
  ewell_user: EwellUser | null;
  created_at: string | null;
  updated_at: string | null;
  deleted_at: string | null;
}

export const DEFAULT_REQUEST_USER: RequestUser = {
  id: 0,
  ewell_user_id: 0,
  organization_id: 0,
  ibow_staff_id: 0,
  display_first_name: "",
  display_last_name: "",
  icon: "",
  icon_url: "",
  role: 0,
  office_mobile_area: "",
  office_mobile_city: "",
  office_mobile_num: "",
  is_office_mobile_public: false,
  system_id: "",
  job_type: 0,
  ewell_user: null,
  created_at: "",
  updated_at: "",
  deleted_at: "",
};

export interface PortalOfficialAnnouncement {
  id: number | null;
  destination: number | null;
  title: string | null;
  body: string | null;
  announced_at: string | null;
  status: number | null;
  is_mail_on: boolean | null;
  has_target: boolean | null;
  target_organizations: PortalOfficialAnnouncementTargetOrganization[] | null;
  target_portal_offices: PortalOfficialAnnouncementTargetOffice[] | null;
  target_recruitees: PortalOfficialAnnouncementTargetRecruitee[] | null;
  display_start_at: string | null;
  display_end_at: string | null;
}

export interface PortalOfficialAnnouncementTargetOrganization {
  id: number | null;
  official_announcement_id: number | null;
  organization_id: number | null;
}

export interface PortalOfficialAnnouncementTargetOffice {
  id: number | null;
  official_announcement_id: number | null;
  portal_office_id: number | null;
}

export interface PortalOfficialAnnouncementTargetRecruitee {
  id: number | null;
  official_announcement_id: number | null;
  jobsearch_recruitee_id: number | null;
}

export const DEFAULT_OFFICIAL_ANNOUNCEMENT: PortalOfficialAnnouncement = {
  id: 0,
  destination: 0,
  title: "",
  body: "",
  announced_at: "",
  status: 0,
  is_mail_on: false,
  has_target: false,
  target_organizations: [],
  target_portal_offices: [],
  target_recruitees: [],
  display_start_at: "",
  display_end_at: "",
};

export interface PortalJobsearchListing extends Base {
  id: number | null;
  portal_office: PortalOffice | null;
  title: string | null;
}

export interface PortalJobsearchApplication extends Base {
  id: number | null;
  portal_office: PortalOffice | null;
  listing_info: PortalJobsearchListing | null;
  jobsearch_recruitee_id: number | null;
  recruitee_name: string | null;
  selection_status: number | null;
  applied_at: string | null;
  accepted_at: string | null;
  joining_date: string | null;
}

export interface PortalJobsearchRecruiter extends Base {
  id: number | null;
  ewell_user_id: number | null;
  ibow_staff_id: number | null;
  portal_office_id: number | null;
  portal_office: PortalOffice | null;
  display_family_name: string | null;
  display_first_name: string | null;
  role: number | null;
  ewell_user?: EwellUser | null;
}

export const DEFAULT_JOBSEARCH_RECRUITER: PortalJobsearchRecruiter = {
  id: 0,
  ewell_user_id: 0,
  ibow_staff_id: 0,
  portal_office_id: 0,
  portal_office: null,
  display_family_name: "",
  display_first_name: "",
  role: 0,
  ewell_user: null,
  created_at: "",
  updated_at: "",
  deleted_at: "",
};

export interface PortalJobsearchRecruitee extends Base {
  id: number | null;
  family_name: string | null;
  first_name: string | null;
  gender: number | null;
  // NOTE: jst_no, full_name, gender_name は vue で加工する値保持用
  jst_no: string | null;
  full_name: string | null;
  gender_name: string | null;
}

export interface JobsearchReferralRegistration {
  id: number;
  referral_code_id: number;
  referrer_ewell_user_id: number;
  referred_ewell_user_id: number;
  referred_recruitee_id: number;
  phone_number: string;
  qualification_registered: boolean;
  status: number;
  approved_by: number;
  approved_at: string;
  completed_at: string;
  admin_note: string;
  created_at: string;
  referrer_name: string;
  referred_name: string;
  referrer_office_name: string;
}

export interface PortalOfficeReview extends Base {
  id: number | null;
  portal_office_review_aspects: PortalOfficeReviewAspect[] | null;
}

export interface PortalOfficeReviewAspect extends Base {
  id: number | null;
}
